import { useCallback } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { CheckIcon, Trash2Icon } from "lucide-react";

import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";

import api from "api";

import type {
  InvitationAcceptResponse,
  NotificationContentProps,
} from "../../types";

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const InvitedActions = ({
  events = [],
  invitations = [],
  onAccept,
  onReject,
}: NotificationContentProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const invitationId = events[0]?.invitation;
  const invitationExists = invitationId
    ? invitations.find((i) => i._id === invitationId && !i.acceptedAt)
    : undefined;

  const acceptInvite = useCallback(() => {
    if (!invitationId) return;
    api.invitations.accept(
      invitationId,
      (data: InvitationAcceptResponse) => {
        onAccept?.(invitationId);
        if (data.group) {
          const { group } = data;
          api.invalidateQueries(api.users.me).then(() => {
            api.invalidateQueries(api.groups.get, [group._id]);
            api.invalidateQueries(api.pages.getAll, ["group", group._id]);
            api.invalidateQueries(api.discussions.getAll, ["group", group._id]);
            navigate(`/groups/${group._id}`);
          });
        }
        toast.success(t("groups.joined"));
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [invitationId, onAccept, navigate, t]);

  const rejectInvite = useCallback(() => {
    if (!invitationId) return;
    api.invitations.reject(
      invitationId,
      () => {
        onReject?.(invitationId);
        toast.success(t("groups.invitationRejected"));
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [invitationId, onReject, t]);

  if (!invitationExists) return null;
  return (
    <ButtonContainer>
      <Button
        color="green"
        size="small"
        onClick={acceptInvite}
        style={{ marginBottom: 5 }}
        aria-label={t("invitation.accept")}
      >
        <Icon icon={CheckIcon} />
      </Button>
      <Button
        size="small"
        onClick={rejectInvite}
        variant="outline"
        aria-label={t("invitation.reject")}
      >
        <Icon icon={Trash2Icon} />
      </Button>
    </ButtonContainer>
  );
};

export default InvitedActions;
